import { Dimensions, PixelRatio, Platform } from 'react-native'
import Constants from 'expo-constants'
import * as Localization from 'expo-localization'
import NetInfo from '@react-native-community/netinfo'
import * as Sentry from '@sentry/react-native'

import { shouldCaptureSentryError } from './auth-errors'

type SeverityLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug'

interface CaptureContext {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  feature?: string
}

type BuildExtra = {
  flavour?: string
  version?: string
  buildNumber?: string
}

type IssueReportDiagnosticsInput = {
  route?: string
  routeParams?: Record<string, string | undefined>
}

type IssueReportDiagnostics = {
  tags: Record<string, string>
  contexts: Record<string, Record<string, unknown>>
  extra: Record<string, unknown>
}

type SubmitGeneralIssueReportInput = IssueReportDiagnosticsInput & {
  message: string
  name?: string
  email?: string
  associatedEventId?: string
  errorMessage?: string
  componentStack?: string
  auth0Subject?: string
  revenueCatAppUser?: string
}

type NormalizedException = {
  error: Error
  extra?: Record<string, unknown>
}

function redactToken(value: string | null | undefined): string {
  if (!value) return '(none)'
  if (value.length <= 8) return '***'
  return `${value.substring(0, 4)}...${value.substring(value.length - 4)}`
}

function hashSensitive(value: string): string {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return `hash:${Math.abs(hash).toString(36)}`
}

function isErrorLike(value: unknown): value is Error {
  return value instanceof Error
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

function normalizeOptionalString(
  value: string | undefined,
): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : undefined
}

function getBuildExtra(): BuildExtra | undefined {
  return Constants.expoConfig?.extra?.build as BuildExtra | undefined
}

function getWebUserAgent(): string | undefined {
  if (Platform.OS !== 'web') {
    return undefined
  }

  const userAgent = globalThis.navigator?.userAgent
  return userAgent == null ? undefined : userAgent.slice(0, 240)
}

function isSentryCaptureEnabled(): boolean {
  return Sentry.getClient()?.getOptions().enabled === true
}

function normalizeResponseLikeError(
  error: Record<string, unknown>,
): Error | null {
  const status = typeof error.status === 'number' ? error.status : null
  const url = typeof error.url === 'string' ? error.url : null
  const statusText =
    typeof error.statusText === 'string' && error.statusText.length > 0
      ? error.statusText
      : null

  if (status == null && url == null) {
    return null
  }

  const messageParts = [
    'HTTP request failed',
    status != null ? `with status ${status}` : null,
    statusText != null ? `(${statusText})` : null,
    url != null ? `for ${url}` : null,
  ].filter(Boolean)

  return new Error(messageParts.join(' '))
}

function normalizeException(error: unknown): NormalizedException {
  if (isErrorLike(error)) {
    return { error }
  }

  // Some fetch/mutation failures bubble up raw response-like objects instead of
  // Error instances. Convert those into stable Error messages so Sentry groups
  // them meaningfully, while preserving the original payload in `extra`.
  if (isRecord(error)) {
    const normalizedResponseError = normalizeResponseLikeError(error)
    if (normalizedResponseError != null) {
      return {
        error: normalizedResponseError,
        extra: {
          normalizedErrorType: 'response-like',
          originalError: error,
        },
      }
    }

    return {
      error: new Error('Captured non-Error exception'),
      extra: {
        normalizedErrorType: 'object',
        originalError: error,
      },
    }
  }

  return {
    error: new Error(
      typeof error === 'string' ? error : 'Captured non-Error exception',
    ),
    extra: {
      normalizedErrorType: typeof error,
      originalError: error,
    },
  }
}

export function captureException(
  error: unknown,
  context?: CaptureContext,
): void {
  if (!shouldCaptureSentryError(error)) {
    return
  }

  const normalized = normalizeException(error)

  Sentry.withScope((scope) => {
    if (context?.feature) {
      scope.setTag('feature', context.feature)
    }
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value)
      }
    }
    if (context?.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value)
      }
    }
    if (normalized.extra) {
      for (const [key, value] of Object.entries(normalized.extra)) {
        scope.setExtra(key, value)
      }
    }
    Sentry.captureException(normalized.error)
  })
}

export function captureMessage(
  message: string,
  level: SeverityLevel = 'info',
  context?: CaptureContext,
): void {
  Sentry.withScope((scope) => {
    if (context?.feature) {
      scope.setTag('feature', context.feature)
    }
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value)
      }
    }
    if (context?.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value)
      }
    }
    Sentry.captureMessage(message, level)
  })
}

export async function collectIssueReportDiagnostics({
  route,
  routeParams,
}: IssueReportDiagnosticsInput = {}): Promise<IssueReportDiagnostics> {
  const buildExtra = getBuildExtra()
  const window = Dimensions.get('window')
  const screen = Dimensions.get('screen')
  const locale = Localization.getLocales()[0]

  let network: Record<string, unknown> = {
    status: 'unavailable',
  }

  try {
    const netInfo = await NetInfo.fetch()
    network = {
      type: netInfo.type,
      isConnected: netInfo.isConnected,
      isInternetReachable: netInfo.isInternetReachable,
      details: netInfo.details,
    }
  } catch (error) {
    network = {
      status: 'failed',
      message: error instanceof Error ? error.message : String(error),
    }
  }

  return {
    tags: {
      'report.source': 'user_issue',
      'report.channel': 'general',
      platform: Platform.OS,
      'build.flavour': buildExtra?.flavour ?? 'unknown',
      'app.version': buildExtra?.version ?? 'unknown',
    },
    contexts: {
      app: {
        flavour: buildExtra?.flavour ?? 'unknown',
        version: buildExtra?.version ?? 'unknown',
        buildNumber: buildExtra?.buildNumber ?? 'unknown',
      },
      device: {
        platform: Platform.OS,
        osVersion: Platform.Version,
        pixelRatio: PixelRatio.get(),
        fontScale: PixelRatio.getFontScale(),
        window,
        screen,
      },
      locale: {
        languageTag: locale?.languageTag,
        languageCode: locale?.languageCode,
        regionCode: locale?.regionCode,
        textDirection: locale?.textDirection,
      },
      network,
    },
    extra: {
      route,
      routeParams,
      webUserAgent: getWebUserAgent(),
    },
  }
}

export async function submitGeneralIssueReport({
  message,
  name,
  email,
  associatedEventId,
  errorMessage,
  componentStack,
  auth0Subject,
  revenueCatAppUser,
  route,
  routeParams,
}: SubmitGeneralIssueReportInput): Promise<boolean> {
  if (!isSentryCaptureEnabled()) {
    return false
  }

  const diagnostics = await collectIssueReportDiagnostics({
    route,
    routeParams,
  })
  const normalizedAuth0Subject = normalizeOptionalString(auth0Subject)
  const normalizedRevenueCatAppUser = normalizeOptionalString(revenueCatAppUser)
  const accountContext: Record<string, Record<string, string>> = {}

  if (normalizedAuth0Subject != null) {
    accountContext.auth0 = { subject: normalizedAuth0Subject }
  }
  if (normalizedRevenueCatAppUser != null) {
    accountContext.revenuecat = { app_user: normalizedRevenueCatAppUser }
  }
  const hasAccountContext = Object.keys(accountContext).length > 0

  // Carry the crash details on the feedback itself so the report is actionable
  // even when the linked event is missing from Sentry.
  const crashContext: Record<string, string> = {}
  const normalizedErrorMessage = normalizeOptionalString(errorMessage)
  const normalizedComponentStack = normalizeOptionalString(componentStack)

  if (normalizedErrorMessage != null) {
    crashContext.message = normalizedErrorMessage
  }
  if (normalizedComponentStack != null) {
    crashContext.componentStack = normalizedComponentStack
  }
  const hasCrashContext = Object.keys(crashContext).length > 0

  Sentry.captureFeedback(
    {
      message: message.trim(),
      name: normalizeOptionalString(name),
      email: normalizeOptionalString(email),
      associatedEventId: normalizeOptionalString(associatedEventId),
    },
    {
      captureContext: {
        tags: {
          ...diagnostics.tags,
          ...(normalizedAuth0Subject != null
            ? { 'auth0.subject': normalizedAuth0Subject }
            : {}),
          ...(normalizedRevenueCatAppUser != null
            ? { 'revenuecat.app_user': normalizedRevenueCatAppUser }
            : {}),
        },
        contexts: {
          ...diagnostics.contexts,
          ...(hasAccountContext ? { account: accountContext } : {}),
          ...(hasCrashContext ? { crash: crashContext } : {}),
        },
        extra: diagnostics.extra,
      },
    },
  )

  return true
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: SeverityLevel = 'info',
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  })
}

export function setUserContext(user: {
  id: string
  email?: string | null
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email ?? undefined,
  })
}

export function clearUserContext(): void {
  Sentry.setUser(null)
}

export const sanitize = {
  token: redactToken,
  sensitive: hashSensitive,
} as const

export const sentryTesting = {
  normalizeException,
  isSentryCaptureEnabled,
} as const
